import { computed, makeObservable, observable, action } from 'mobx';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Amount } from 'src_common/common/amount/Amount';
import {
    validatePositive,
    validateAmountRequire,
    validateAmountOrNothing,
    validatePositiveOrNothing,
} from 'src/domains/players/shared/validatorsNew/validateRequire';
import addError from 'src/utils/error.jsx';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';
import { FilterOptionType, FilterValueType } from 'src/components/RealityCheckForm/CustomDropdown';
import { convertToNumber, validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { getRollingLimits, getDepositLimits, putRollingLimits } from 'src_server/types/responsibleGambling';
import confirmation from 'src/utils/confirm.jsx';

function groupDataByWindowLength(
    data: getRollingLimits.LimitsObjectSchemaType[]
): Map<number, getRollingLimits.LimitsObjectSchemaType> {
    return new Map(
        data
            .sort((a, b) => a.windowLengthInDays - b.windowLengthInDays)
            .map((activeObj) => [activeObj.windowLengthInDays, activeObj])
    );
}

export const FULL_OPTION_LENGTH_DAYS_OPTIONS_ARRAY: FilterOptionType<string>[] = [
    {
        value: '0',
        label: 'Lifetime',
    },
    {
        value: '1',
        label: '1 day',
    },
    {
        value: '7',
        label: '7 days',
    },
    {
        value: '30',
        label: '30 days',
    },
    {
        value: '90',
        label: '90 days',
    },
    {
        value: '365',
        label: '365 days',
    },
];

export const LIMIT_TYPE_ARRAY: FilterOptionType<string>[] = [
    {
        value: 'calendar',
        label: 'Calendar',
    },
    {
        value: 'rolling',
        label: 'Rolling',
    },
];

export type DepositLimitCalculationType = 'gross' | 'net';

export type DepositLimitDashboardType = 'gross' | 'net';

export const CALCULATION_TYPE_ARRAY: FilterOptionType<string>[] = [
    { value: 'net', label: 'Net deposit' },
    { value: 'gross', label: 'Gross deposit' },
];

export const DEFAULT_ROW = 100;
export class NetDepositLimitState {
    @observable public selectedEditRow: number = DEFAULT_ROW;

    public readonly getRollingLimitsResource: Resource<getRollingLimits.ResponseType>;
    public readonly getDepositLimitsResource: Resource<getDepositLimits.ResponseType>;

    public readonly selectedPeriod: FormInputState<string, number>;
    public readonly limitType: FormInputState<string, string>;
    public readonly calculationType: FormInputState<string, string>;
    public readonly limitValue: FormInputState<string, Amount>;
    public readonly limitValueMax: FormInputState<string, Amount | string>;
    public readonly triggersWarningEmail: FormInputState<boolean, boolean>;
    public readonly isHardLimit: FormInputState<boolean, boolean>;
    public readonly form: FormModel<Omit<putRollingLimits.LimitObjectType, 'limitType' | 'calculationType'>>;

    public constructor(
        private readonly trpcClient: TrpcClient,
        private readonly customerId: () => number,
        private readonly permissionDepositLimits: boolean,
        private readonly configState: ConfigState,
        private readonly dashboard: DepositLimitDashboardType
    ) {
        makeObservable(this);

        this.getRollingLimitsResource = new Resource(async (): Promise<getRollingLimits.ResponseType> => {
            const response = await this.trpcClient.client.responsibleGamblingRouter.getRollingLimits.query({
                accountId: this.customerId(),
                calculationType: this.lockedCalculationType,
                ...(this.dashboard === 'gross' ? { limitType: 'calendar' as const } : {}),
            });
            return response;
        });

        this.getDepositLimitsResource = new Resource(async (): Promise<getDepositLimits.ResponseType> => {
            const response = await this.trpcClient.client.responsibleGamblingRouter.getDepositLimits.query({
                accountId: this.customerId(),
            });
            return response;
        });

        this.selectedPeriod = FormInputState.new('')
            .map(validateNotEmpty('Please select restriction period.'))
            .map(convertToNumber('This field is required.'));
        this.limitType = FormInputState.new(dashboard === 'gross' ? 'calendar' : '').map(
            validateNotEmpty('Please select limit type.')
        );
        this.calculationType = FormInputState.new(dashboard === 'gross' ? 'gross' : 'net');

        this.limitValue = FormInputState.new('').map(validateAmountRequire).map(validatePositive);
        this.limitValueMax = FormInputState.new('').map(validateAmountOrNothing).map(validatePositiveOrNothing);
        this.triggersWarningEmail = FormInputState.new(false);
        this.isHardLimit = FormInputState.new(false);

        this.form = FormModel.group({
            windowLengthInDays: this.selectedPeriod,
            limitType: this.limitType,
            pendingLimitValue: this.limitValue.map((amount) => Result.createOk(parseInt(amount.multiply(100).value))),
            limitValueMax: this.limitValueMax.map((amount) =>
                Result.createOk(typeof amount === 'string' ? null : parseInt(amount.multiply(100).value))
            ),
            triggersWarningEmail: this.triggersWarningEmail,
            hardLimit: this.isHardLimit,
        });
    }

    public get lockedCalculationType(): DepositLimitCalculationType {
        return this.dashboard === 'gross' ? 'gross' : 'net';
    }

    @computed public get calculationTypeOptions(): FilterOptionType<string>[] {
        return CALCULATION_TYPE_ARRAY.filter((option) => option.value === this.lockedCalculationType);
    }

    public getCalculationTypeForRow(windowLengthInDays: number): DepositLimitCalculationType {
        return this.getRollingLimits.get(windowLengthInDays)?.calculationType ?? this.lockedCalculationType;
    }

    public renderAmount(currency: CurrencyType, usedLimit: number | null): string {
        const usedAmountWithCurrency =
            usedLimit === null ? 'not set' : this.configState.precision.newFromOld(usedLimit).format(currency);
        return usedAmountWithCurrency;
    }

    @computed public get create_window_length_days_options_array(): FilterOptionType<string>[] {
        const actualLimits = [...this.getRollingLimits.keys()];
        const result = FULL_OPTION_LENGTH_DAYS_OPTIONS_ARRAY.filter((option) => {
            if (this.dashboard === 'gross' && option.value === '0') {
                return false;
            }
            return actualLimits.some((element) => element === parseInt(option.value)) === false;
        });

        return result;
    }
    @computed public get isLimitTypeLockedToCalendar(): boolean {
        return this.dashboard === 'gross';
    }

    @action private syncLimitTypeWithCalculationType = (): void => {
        if (this.isLimitTypeLockedToCalendar) {
            this.limitType.setValue('calendar');
        }
    };

    @action public onUpdatePeriod = (changedValue: FilterValueType): void => {
        if (typeof changedValue === 'string') {
            if (this.dashboard === 'gross' && changedValue === '0') {
                return;
            }
            if (changedValue === '0') {
                this.onUpdateType('lifetime');
            } else if (this.limitType.value === 'lifetime') {
                this.onUpdateType(this.isLimitTypeLockedToCalendar ? 'calendar' : '');
            }
            this.selectedPeriod.setValue(changedValue);
            this.syncLimitTypeWithCalculationType();
        }
    };

    @action public onUpdateType = (changedValue: FilterValueType): void => {
        if (typeof changedValue !== 'string') {
            return;
        }
        if (this.isLimitTypeLockedToCalendar && changedValue !== 'calendar') {
            this.limitType.setValue('calendar');
            return;
        }
        this.limitType.setValue(changedValue);
    };

    @action public onUpdateCalculationType = (changedValue: FilterValueType): void => {
        if (changedValue !== this.lockedCalculationType) {
            this.calculationType.setValue(this.lockedCalculationType);
        }
        this.syncLimitTypeWithCalculationType();
    };

    @computed public get getRollingLimits(): Map<number, getRollingLimits.LimitsObjectSchemaType> {
        const response = this.getRollingLimitsResource.get();

        if (response.type === 'ready' && response.value.responseStatus === 'success') {
            return groupDataByWindowLength(response.value.data);
        } else if (response.type === 'error') {
            addError('We seem to have a problem to fetch data');
        } else if (response.type === 'ready' && response.value.responseStatus === 'apiServerError') {
            addError(`Get Rolling net deposit limits: ${response.value.data.message}`);
        } else if (response.type === 'ready' && response.value.responseStatus === 'apiServerSecondError') {
            addError(`Get Rolling net deposit limits: ${response.value.data.error.message}`);
        }

        return new Map();
    }

    @computed private get depositLimitUsages(): getDepositLimits.LimitsDetailsObjectSchemaType[] {
        const response = this.getDepositLimitsResource.get();
        if (response.type === 'ready' && response.value.responseStatus === 'success') {
            return response.value.data.limits;
        }
        return [];
    }

    private findDepositLimitUsage(
        windowLengthInDays: number,
        limitType?: putRollingLimits.LimitObjectType['limitType'],
        calculationType?: DepositLimitCalculationType
    ): getDepositLimits.LimitsDetailsObjectSchemaType | undefined {
        const effectiveLimitType = limitType ?? this.getRollingLimits.get(windowLengthInDays)?.limitType;
        const effectiveCalculationType =
            calculationType ??
            this.getRollingLimits.get(windowLengthInDays)?.calculationType ??
            this.lockedCalculationType;
        return this.depositLimitUsages.find(
            (limitItem) =>
                limitItem.windowLengthInDays === windowLengthInDays &&
                (effectiveLimitType === undefined || limitItem.type === effectiveLimitType) &&
                limitItem.calculationType === effectiveCalculationType
        );
    }

    private getUsedLimit(
        windowLengthInDays: number,
        limitType?: putRollingLimits.LimitObjectType['limitType'],
        calculationType?: DepositLimitCalculationType
    ): number | null {
        return this.findDepositLimitUsage(windowLengthInDays, limitType, calculationType)?.used ?? null;
    }

    public getLimitType(windowLengthInDays: number): putRollingLimits.LimitObjectType['limitType'] | undefined {
        return (
            this.getRollingLimits.get(windowLengthInDays)?.limitType ??
            this.findDepositLimitUsage(windowLengthInDays)?.type
        );
    }

    public usedLimit = (
        currency: CurrencyType,
        windowLengthInDays: number,
        limitType?: putRollingLimits.LimitObjectType['limitType'],
        calculationType?: DepositLimitCalculationType
    ): string => {
        const usedLimit = this.getUsedLimit(windowLengthInDays, limitType, calculationType);
        return usedLimit === null ? 'n/a' : this.renderAmount(currency, usedLimit);
    };

    public remainingLimit = (
        currency: CurrencyType,
        windowLengthInDays: number,
        activeLimitValue: number,
        limitType?: putRollingLimits.LimitObjectType['limitType'],
        calculationType?: DepositLimitCalculationType
    ): string => {
        const usedLimit = this.getUsedLimit(windowLengthInDays, limitType, calculationType);
        return usedLimit === null ? 'n/a' : this.renderAmount(currency, activeLimitValue - usedLimit);
    };

    @action public onEditRow = (index: number): null | void => {
        this.selectedEditRow = index;

        this.selectedPeriod.setValue(this.getRollingLimits.get(index)?.windowLengthInDays.toString() ?? '');
        this.limitType.setValue(this.getRollingLimits.get(index)?.limitType ?? '');
        this.calculationType.setValue(this.lockedCalculationType);
        this.syncLimitTypeWithCalculationType();
        this.limitValue.setValue(this.divideAmount(this.getRollingLimits.get(index)?.activeLimitValue ?? 0));
        this.limitValueMax.setValue(this.divideAmount(this.getRollingLimits.get(index)?.limitValueMax ?? 0));
        this.triggersWarningEmail.setValue(this.getRollingLimits.get(index)?.triggersWarningEmail ?? false);
        this.isHardLimit.setValue(this.getRollingLimits.get(index)?.hardLimit ?? false);
    };

    private divideAmount = (amount: number): string => {
        return new Amount(amount.toString()).div(100).value;
    };

    @action public onCancel = (): void => {
        this.resetForm();
    };

    @action private resetForm = (): void => {
        this.selectedEditRow = DEFAULT_ROW;

        this.selectedPeriod.reset();
        this.limitType.reset();
        this.calculationType.reset();
        this.limitValue.reset();
        this.limitValueMax.reset();
        this.triggersWarningEmail.reset();
        this.isHardLimit.reset();
    };

    private getFormValue = (): putRollingLimits.LimitObjectType | null => {
        this.form.setAsVisited();
        const { result } = this.form;

        if (result.value.type === 'ok') {
            const parsedLimitType = putRollingLimits.limitTypeSchema.safeParse(
                this.dashboard === 'gross' ? 'calendar' : this.limitType.value
            );
            if (parsedLimitType.success === false) {
                return null;
            }

            const parsedLimitValueMax = result.value.data.limitValueMax === 0 ? null : result.value.data.limitValueMax;

            return {
                ...result.value.data,
                limitValueMax: parsedLimitValueMax,
                limitType: parsedLimitType.data,
                calculationType: this.lockedCalculationType,
            };
        } else if (Array.isArray(result.value.message)) {
            addError(`Cannot submit form`);
        }
        return null;
    };

    @action public onSubmit = async (): Promise<void> => {
        if (this.permissionDepositLimits === true) return;

        const resultForm = this.getFormValue();
        if (resultForm === null) {
            console.log('error submit!');
            return;
        }

        if (resultForm.limitValueMax === null)
            await confirmation(`Are you sure that you want to remove Max limit value`, 'ok');

        try {
            const existingLimit =
                this.selectedEditRow === DEFAULT_ROW ? undefined : this.getRollingLimits.get(this.selectedEditRow);
            const payload: putRollingLimits.InputType['payload'] =
                existingLimit === undefined
                    ? { source: 'backoffice', list: [resultForm] }
                    : {
                          source: 'backoffice',
                          list: [resultForm],
                          windowLengthInDays: existingLimit.windowLengthInDays,
                          limitType: existingLimit.limitType,
                          calculationType: existingLimit.calculationType,
                      };

            const response = await this.trpcClient.client.responsibleGamblingRouter.putRollingLimits.mutate({
                accountId: this.customerId(),
                payload,
            });

            if (response.responseStatus === 'success') {
                this.resetForm();
                await this.getRollingLimitsResource.refresh();
                await this.getDepositLimitsResource.refresh();
            } else if (response.responseStatus === 'error') {
                if (response.data.error.code === 'Limit update error') {
                    addError('New limit cannot be higher than Max limit');
                    return;
                }
                addError(response.data.error.message);
            } else {
                addError('There was a problem with request response');
            }
        } catch (err) {
            addError('There was a problem with request');
            console.log(err);
        }
    };

    @action public onDelete = async (): Promise<void> => {
        if (this.permissionDepositLimits === true) return;

        await confirmation(`Are you sure that you want to remove this deposit limit?`, 'ok');
        const windowLengthInDays = parseInt(this.selectedPeriod.value, 10);
        const limitType = this.getLimitType(windowLengthInDays);

        if (limitType === undefined) {
            addError('There is no limit type defined. Please report this problem');
            return;
        }
        try {
            const response = await this.trpcClient.client.responsibleGamblingRouter.deleteLimits.mutate({
                accountId: this.customerId(),
                payload: {
                    windowLengthInDays,
                    limitType,
                    calculationType: this.getCalculationTypeForRow(windowLengthInDays),
                },
            });

            if (response.responseStatus === 'success') {
                this.resetForm();
                await this.getRollingLimitsResource.refresh();
                await this.getDepositLimitsResource.refresh();
            } else {
                if (response.responseStatus === 'error') {
                    if (response.data.code === 'unauthorized_client') {
                        addError("You don't have required permissions to perform this action");
                        return;
                    }
                }
                addError('There was a problem with request response');
            }
        } catch (err) {
            addError('There was a problem with request');
            console.log(err);
        }
    };
}
